import { Copy, Edit, GripVertical } from 'lucide-react';
import { DynamicIcon } from 'lucide-react/dynamic';
import { Badge, Button, Card, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { Card as CardType, GoalWithStatus } from '../../service/api';
import CardActionsList from '../CardActionsList';
import type { DragHandleProps } from '../SortableList';
import UserAvatar from '../UserAvatar';

interface ManagerGoalCardProps {
  goal: GoalWithStatus;
  /** Optional drag handle props — when provided, renders a grip icon for reordering */
  dragHandleProps?: DragHandleProps;
}

export default function ManagerGoalCard({
  goal: { goal, status, userStatuses },
  dragHandleProps,
}: ManagerGoalCardProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const getSelectorText = () => {
    const types = goal.config.selectors.map(selector => selector.type);
    const uniqueTypes = [...new Set(types)];

    if (uniqueTypes.length === 1) {
      return uniqueTypes[0] === 'task'
        ? t('manager.goals.taskBased')
        : t('manager.goals.achievementBased');
    }

    return t('manager.goals.mixed'); // For mixed types
  };

  const getSequenceTypeText = () => {
    if (goal.config.sequenceType === 'sequential') {
      return t('goals.sequential');
    }
    return t('goals.cumulative');
  };

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();

    // Navigate to create goal page with copied data
    navigate('/manager/create-goal', {
      state: {
        copyFrom: goal,
      },
    });
  };

  // Use userStatuses if available (for managers), otherwise fall back to single status display
  const hasUserStatuses = userStatuses && userStatuses.length > 0;

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/manager/goal/${goal._id}`);
  };

  return (
    <Card className="mb-3 shadow-sm border">
      <Card.Body>
        <Row className="align-items-center">
          {dragHandleProps && (
            <Col
              xs="auto"
              className="d-flex align-items-center pe-0"
              style={dragHandleProps.style}
              {...dragHandleProps.listeners}
              {...dragHandleProps.attributes}
            >
              <GripVertical size={20} className="text-muted" />
            </Col>
          )}
          <Col xs="auto">
            <DynamicIcon name={goal.icon} size={32} className="text-primary" />
          </Col>
          <Col>
            <h6 className="mb-0">{goal.title}</h6>
          </Col>
          <Col xs="auto" className="d-flex align-items-center gap-2">
            {typeof goal.rewardCard === 'string' ? (
              <Badge bg="primary">Card Preset</Badge>
            ) : (goal.rewardCard as CardType)?.actions?.length > 0 ? (
              <CardActionsList
                actions={(goal.rewardCard as CardType).actions}
                size="xs"
              />
            ) : (
              <Badge bg="secondary">No reward</Badge>
            )}
          </Col>
          <Col xs="auto" className="d-flex gap-1">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleEdit}
              title={t('common.edit')}
            >
              <Edit size={16} />
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleCopy}
              title={t('common.createCopy')}
            >
              <Copy size={16} />
            </Button>
          </Col>
        </Row>

        {/* User avatars with individual progress - compact single line */}
        <Row className="align-items-center pt-3">
          <Col>
            <div
              className="d-flex flex-wrap align-items-center text-muted"
              style={{ fontSize: '0.8rem' }}
            >
              {hasUserStatuses ? (
                userStatuses.map(({ user, status: userStatus }, index) => (
                  <span
                    key={user._id}
                    className="d-inline-flex align-items-center"
                  >
                    {index > 0 && <span className="mx-2">|</span>}
                    <UserAvatar user={user} size={20} className="me-1" />
                    <span>
                      {Math.round(userStatus.percentage)}% ({userStatus.current}
                      /{userStatus.required})
                    </span>
                  </span>
                ))
              ) : (
                // Fallback for when userStatuses is not available (e.g., managed users)
                <div className="d-flex align-items-center">
                  {goal.assignedTo.slice(0, 3).map((user, index) => (
                    <UserAvatar
                      key={user._id}
                      user={user}
                      size={20}
                      className="me-1"
                      style={{ marginLeft: index > 0 ? '-8px' : '0' }}
                    />
                  ))}
                  {goal.assignedTo.length > 3 && (
                    <span className="ms-1">+{goal.assignedTo.length - 3}</span>
                  )}
                  <span className="ms-2">
                    {Math.round(status.percentage)}% ({status.current}/
                    {status.required})
                  </span>
                </div>
              )}
            </div>
          </Col>
        </Row>

        <Row className="align-items-center pt-2 pb-2">
          <Col>
            {goal.description && (
              <p className="text-muted small mb-1">{goal.description}</p>
            )}
          </Col>
        </Row>
        <Row className="align-items-center">
          <Col>
            <div className="d-flex gap-2 mt-1">
              <Badge bg="secondary" className="fs-7">
                {getSelectorText()}
              </Badge>
              <Badge bg="info" className="fs-7">
                {getSequenceTypeText()}
              </Badge>
              {goal.config.repeatable && (
                <Badge bg="success" className="fs-7">
                  {t('goals.repeatable')}
                </Badge>
              )}
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}
